/**
 * components/autotrader/ShadowLog.tsx
 * "What the skipped names did" — the shadow log summary. Every candidate the
 * autotrader looked at and did NOT trade is recorded with its price at the
 * decision; after the close the backend fills in where the stock (and the
 * option, when one was identified) ended up. This card summarizes that by skip
 * reason and score band, so the breakout rule, the minimum score and the
 * liquidity gate can be judged against evidence instead of guesses. It is
 * observation only — nothing here changes any trading decision.
 * Fetches independently of the tab's status polling, like PnLDashboard.
 */

import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { autotraderApi } from '../../lib/api';
import type { ShadowSummary, ShadowAgg } from '../../types';

const REASON_LABELS: Record<string, string> = {
  'entered':      'Entered (the comparison group)',
  'no-breakout':  'Failed the breakout check',
  'below-min':    'Scored below the minimum',
  'no-liquid':    'No liquid option',
  'too-small':    'Position size too small',
  'order-failed': 'Order placement failed',
};

const DAY_OPTIONS = [7, 30, 90];

function pctCell(v: number | null): JSX.Element {
  if (v == null) return <span className="text-ink-3">—</span>;
  return <span className={clsx(v > 0 ? 'text-bull' : v < 0 ? 'text-bear' : 'text-ink-2')}>{v > 0 ? '+' : ''}{v.toFixed(1)}%</span>;
}

function AggTable({ title, rows, labelOf }: { title: string; rows: ShadowAgg[]; labelOf?: (l: string) => string }) {
  if (!rows.length) return null;
  return (
    <div className="overflow-x-auto">
      <div className="text-2xs text-ink-3 mb-1">{title}</div>
      <table className="w-full text-2xs font-mono">
        <thead>
          <tr className="text-ink-3 text-left">
            <th className="font-normal pr-2 py-0.5">Group</th>
            <th className="font-normal pr-2 text-right">Names</th>
            <th className="font-normal pr-2 text-right">Closed up</th>
            <th className="font-normal pr-2 text-right">Avg to close</th>
            <th className="font-normal pr-2 text-right">Avg to high</th>
            <th className="font-normal pr-2 text-right">SPY</th>
            <th className="font-normal pr-2 text-right">vs SPY</th>
            <th className="font-normal text-right">Avg option</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.label} className="border-t border-border/30">
              <td className="pr-2 py-0.5 text-ink-1">{labelOf ? labelOf(r.label) : r.label}</td>
              <td className="pr-2 text-right text-ink-2">{r.n}{r.resolved < r.n ? <span className="text-ink-3"> ({r.resolved})</span> : null}</td>
              <td className="pr-2 text-right">{r.pct_up == null ? <span className="text-ink-3">—</span> : `${r.pct_up.toFixed(0)}%`}</td>
              <td className="pr-2 text-right">{pctCell(r.avg_close_pct)}</td>
              <td className="pr-2 text-right">{pctCell(r.avg_high_pct)}</td>
              <td className="pr-2 text-right">{pctCell(r.avg_spy_pct)}</td>
              <td className="pr-2 text-right">{pctCell(r.avg_vs_spy)}</td>
              <td className="text-right">{pctCell(r.avg_option_pct)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ShadowLog() {
  const [days, setDays]       = useState(30);
  const [data, setData]       = useState<ShadowSummary | null>(null);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    autotraderApi.shadow(days)
      .then(d => { if (!cancelled) { setData(d); setError(null); } })
      .catch(e => { if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load the shadow log'); });
    return () => { cancelled = true; };
  }, [days]);

  const total    = data?.byReason.reduce((s, r) => s + r.n, 0) ?? 0;
  const resolved = data?.byReason.reduce((s, r) => s + r.resolved, 0) ?? 0;

  return (
    <div className="card">
      <div className="section-label mb-2 flex items-center justify-between gap-2">
        <span>What the skipped names did</span>
        <div className="flex gap-1">
          {DAY_OPTIONS.map(d => (
            <button key={d} onClick={() => setDays(d)}
              className={clsx('px-2 py-0.5 rounded text-2xs font-mono border',
                days === d ? 'border-sky text-sky' : 'border-border text-ink-3')}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      {error && <div className="text-2xs text-bear">{error}</div>}
      {!data && !error && <div className="text-2xs text-ink-3">Loading…</div>}

      {data && total === 0 && (
        <div className="text-2xs text-ink-3 leading-relaxed">
          Nothing recorded yet. Each trading day, every name the Auto-Trader looks at and doesn't trade is logged,
          and its result is filled in after the close.
        </div>
      )}

      {data && total > 0 && (
        <div className="space-y-3">
          <div className="text-2xs text-ink-3 leading-relaxed">
            {total} skipped name{total !== 1 ? 's' : ''} in the last {data.days} days, {resolved} with end-of-day results
            (counts in brackets are the resolved ones). "Avg to close" and "Avg to high" are the stock's move from the
            decision price; "SPY" is the market's move over the same stretch of the same days and "vs SPY" is the
            names' average minus it (blank before 10/29, when the baseline started); "Avg option" is the option's
            mid price, where a contract was identified.
          </div>
          <AggTable title="By skip reason" rows={data.byReason} labelOf={l => REASON_LABELS[l] ?? l} />
          <AggTable title="By score (names that were scored)" rows={data.byScore} />
          {data.recent.length > 0 && (
            <div className="overflow-x-auto">
              <div className="text-2xs text-ink-3 mb-1">Most recent results</div>
              <table className="w-full text-2xs font-mono">
                <tbody>
                  {data.recent.map(r => (
                    <tr key={`${r.scan_date}-${r.sym}`} className="border-t border-border/30">
                      <td className="pr-2 py-0.5 text-ink-3">{r.scan_date.slice(5)}</td>
                      <td className="pr-2 text-ink-1">{r.sym}</td>
                      <td className="pr-2 text-ink-3">{REASON_LABELS[r.reason_code] ?? r.reason_code}{r.score != null ? ` · ${r.score}` : ''}</td>
                      <td className="pr-2 text-right">{pctCell(r.eod_stock_pct)}</td>
                      <td className="text-right">{pctCell(r.eod_option_pct)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
